// HEAL-44a §5.1 — экран одного анализа.
//
// До HEAL-44 строка в «Моих загрузках» была тупиком: провалиться некуда, файл не
// открыть, показатели не посмотреть, ничего не удалить. Этот экран и есть то,
// что за строкой.

import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { AlertTriangle, ChevronRight, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useDeleteUpload, useUpdateUpload, useUpload } from '@/api/hooks/uploads';
import { useUploadErrorToast } from '@/api/error-toast';
import { useToast } from '@/hooks/use-toast';
import { capitalizeFirst } from '@/lib/utils';
import {
  BORDER_LEFT_BY_TIER,
  displayTier,
  reviewDisplayTier,
  VALUE_TEXT_BY_TIER,
} from '@/lib/biomarkerStatus';
import {
  FAILURE_HINT,
  STATUS_LABEL,
  formatDate,
  formatSize,
  pluralMarkers,
  uploadTitle,
} from '@/lib/uploadDisplay';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { setReviewOrigin, uploadParent, UPLOADS_SECTION } from '@/lib/uploadNav';
import { SummaryBarChart } from '@/components/dashboard/SummaryBarChart';
import { MaskedDateInput, isRealIsoDate } from '@/components/forms/MaskedDateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ViewBlankButton } from '@/components/uploads/ViewBlankButton';
import { UploadStatusChip } from '@/components/uploads/UploadStatusChip';
import type { AxiosError } from 'axios';
import type {
  CompositionPoint,
  EnrichedBiomarker,
  GetUploadResponse,
  ReviewSummary,
  Upload,
  UploadApiError,
} from '@/api/types';

export function UploadDetail({ uploadId }: { uploadId: number }) {
  const { data, error, isLoading, isError } = useUpload(uploadId);
  const [, setLocation] = useLocation();

  if (isLoading) {
    return (
      <MobileFrame>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileFrame>
    );
  }

  if (isError || !data?.upload) {
    // HEAL-81: «нет такого анализа» и «связь оборвалась» — разные вещи.
    //
    // Раньше оба случая говорили «Не удалось загрузить анализ»: человек, открывший
    // чужую или устаревшую ссылку, читал это как сбой приложения и жал «обновить»
    // снова и снова. Само ограничение работает — API на чужой анализ отдаёт 404
    // (Upload::resolveRouteBinding), — молчал только экран.
    //
    // Про доступ и про несуществующий анализ говорим одной фразой намеренно:
    // раздельные тексты подсказали бы чужому, что такой анализ существует.
    const notFound = error?.response?.status === 404;

    return (
      <MobileFrame>
        <ScreenHeader
          onBack={() => setLocation(UPLOADS_SECTION, { replace: true })}
          title="Анализ"
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {notFound
              ? 'Анализ не найден или у вас нет доступа.'
              : 'Не удалось загрузить анализ.'}
          </p>
        </div>
      </MobileFrame>
    );
  }

  return <UploadDetailReady response={data} />;
}

function UploadDetailReady({ response }: { response: GetUploadResponse }) {
  const upload = response.upload;
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const showError = useUploadErrorToast();

  const updateMutation = useUpdateUpload(upload.id);
  const deleteMutation = useDeleteUpload(upload.id);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [shiftWarning, setShiftWarning] = useState<{ date: string; markers: number } | null>(null);

  const composition = response.composition;
  const grouped = useMemo(() => groupByCategory(composition?.points ?? []), [composition]);
  const pointsCount = composition?.points.length ?? 0;

  // HEAL-82: возврат в РАЗДЕЛ, а не шаг назад по истории браузера.
  //
  // Прежний `window.history.back()` не знал, куда ведёт. После удаления он
  // приводил на уже удалённый анализ («не удалось загрузить»), а при заходе по
  // внешней ссылке — вообще наружу из приложения.
  //
  // Родитель зависит от того, из пакета ли анализ (круг ревью, находка 1):
  // жёсткая «история загрузок» выбрасывала бы человека из пакета, куда у
  // завершённого пакета другого входа в интерфейсе нет. Это ровно тот дефект,
  // который задача чинит, — только на пути «пакет → анализ».
  //
  // Замена записи, а не добавление: иначе аппаратная кнопка «назад» на телефоне
  // отменяет переход и возвращает на экран, который человек только что покинул.
  const goBack = () => setLocation(uploadParent(upload.batch_id), { replace: true });

  const saveTitle = async (title: string) => {
    try {
      await updateMutation.mutateAsync({ title });
    } catch (e) {
      showError(e as AxiosError<UploadApiError>);
    }
  };

  const saveDate = async (measured_at: string, confirmShift = false) => {
    try {
      await updateMutation.mutateAsync({
        measured_at,
        ...(confirmShift ? { confirm_shift: true } : {}),
      });
      setShiftWarning(null);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { error?: string; affected_markers?: number } } };

      // Смена даты переставляет анализ в истории и может изменить, какое значение
      // показателя считается текущим. Действие невидимо, последствие масштабно —
      // поэтому спрашиваем, а не делаем молча.
      if (err.response?.data?.error === 'requires_shift_confirmation') {
        setShiftWarning({
          date: measured_at,
          markers: err.response.data.affected_markers ?? 0,
        });
        return;
      }

      showError(e as AxiosError<UploadApiError>);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      setConfirmDeleteOpen(false);
      toast({ title: 'Анализ удалён', variant: 'success' });
      // ЗАМЕНА записи в истории, а не добавление (HEAL-82): иначе удалённый
      // анализ остаётся в ней, и системный жест «назад» возвращает человека на
      // экран того, чего уже нет.
      //
      // И сразу на конечный адрес, минуя перенаправляющий `/uploads`: каждый
      // проход через него добавлял ещё одну запись и создавал петлю, в которой
      // кнопка «Назад» выглядела мёртвой.
      setLocation(uploadParent(upload.batch_id), { replace: true });
    } catch (e) {
      setConfirmDeleteOpen(false);
      showError(e as AxiosError<UploadApiError>);
    }
  };

  const status = STATUS_LABEL[upload.status];

  return (
    <MobileFrame>
      <ScreenHeader onBack={goBack} title="Анализ" />

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <UploadHeaderCard
          upload={upload}
          status={status}
          onSaveTitle={saveTitle}
          onSaveDate={saveDate}
          saving={updateMutation.isPending}
        />

        {upload.status === 'awaiting_user_review' && (
          <Button
            onClick={() => {
              // Человек разбирает анализ из раздела — «Вернусь позже» вернёт его
              // сюда же, а не выбросит на Главную.
              setReviewOrigin('history');
              setLocation(`/uploads/${upload.id}/review`);
            }}
            size="lg"
          >
            Проверить и подтвердить
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}

        {upload.status === 'reported' && (
          <p className="rounded-surface bg-slate-50 p-3 text-hl-secondary text-slate-600">
            Мы проверяем этот анализ по вашей жалобе. Ответим, когда разберёмся.
          </p>
        )}

        {upload.status === 'failed' && (
          <div className="flex items-start gap-2 rounded-surface bg-rose-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
            <div className="text-hl-secondary text-rose-800">
              <div className="font-semibold">
                {upload.failure_reason ? FAILURE_HINT[upload.failure_reason] : 'Не удалось обработать файл'}
              </div>
              <p className="mt-0.5 text-rose-700">Попробуйте загрузить файл ещё раз.</p>
            </div>
          </div>
        )}

        {upload.status === 'processing' && (
          <div className="flex items-center gap-2 rounded-surface bg-slate-50 p-3 text-hl-secondary text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Распознаём показатели — это займёт пару минут.
          </div>
        )}

        {/* HEAL-44a: у анализа с жалобой точек ещё нет, но распознанное есть —
            бэкенд отдаёт его в enriched_biomarkers. Без этой ветки человек видел
            пояснение о проверке и ни одной строки, то есть не понимал, на что
            вообще пожаловался. */}
        {!composition && response.enriched_biomarkers && response.summary && (
          <ReviewedComposition
            markers={response.enriched_biomarkers}
            summary={response.summary}
          />
        )}

        {composition && composition.summary.total > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Сводка</h2>
            <SummaryBarChart
              total={composition.summary.total}
              normal={composition.summary.normal}
              attention={composition.summary.attention}
              outOfRange={composition.summary.out_of_range}
              unknown={composition.summary.unknown}
            />
          </section>
        )}

        {composition && (
          <section className="flex flex-col gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {pointsCount > 0
                ? `${pointsCount} ${pluralMarkers(pointsCount)} из этого файла`
                : 'Показатели'}
            </h2>

            {pointsCount === 0 ? (
              <p className="rounded-surface bg-muted p-3 text-hl-secondary text-muted-foreground">
                Показатели не распознаны.
              </p>
            ) : (
              grouped.map(({ category, label, items }) => (
                <div key={category} className="flex flex-col gap-1.5">
                  <div className="text-hl-small font-medium text-gray-400">{label}</div>
                  {items.map((point) => (
                    <CompositionRow key={point.id} point={point} />
                  ))}
                </div>
              ))
            )}
          </section>
        )}

        <div className="mt-1 flex flex-col gap-2">
          <ViewBlankButton
            uploadId={upload.id}
            fileSize={upload.file_size}
            mimeType={upload.mime_type}
            corrupted={upload.failure_reason === 'corrupted'}
          />

          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirmDeleteOpen(true)}
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Удалить анализ
          </Button>
        </div>
      </div>

      <DeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        pointsCount={pointsCount}
        isReported={upload.status === 'reported'}
        pending={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      <ShiftDialog
        warning={shiftWarning}
        pending={updateMutation.isPending}
        onCancel={() => setShiftWarning(null)}
        onConfirm={() => shiftWarning && saveDate(shiftWarning.date, true)}
      />
    </MobileFrame>
  );
}

function UploadHeaderCard({
  upload,
  status,
  onSaveTitle,
  onSaveDate,
  saving,
}: {
  upload: Upload;
  status: { text: string; tone: 'ok' | 'warn' | 'wait' | 'hold' | 'err' };
  onSaveTitle: (title: string) => void;
  onSaveDate: (date: string) => void;
  saving: boolean;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(uploadTitle(upload));
  const [editingDate, setEditingDate] = useState(false);

  const commitTitle = () => {
    const next = titleDraft.trim();
    setEditingTitle(false);

    if (next === '' || next === uploadTitle(upload)) {
      setTitleDraft(uploadTitle(upload));
      return;
    }

    onSaveTitle(next);
  };

  return (
    <div className="flex flex-col gap-2 rounded-surface border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        {editingTitle ? (
          <Input
            autoFocus
            value={titleDraft}
            maxLength={120}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitle();
              if (e.key === 'Escape') {
                setTitleDraft(uploadTitle(upload));
                setEditingTitle(false);
              }
            }}
            aria-label="Название анализа"
            className="text-hl-section font-semibold"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setTitleDraft(uploadTitle(upload));
              setEditingTitle(true);
            }}
            className="flex min-w-0 flex-1 items-start gap-1.5 text-left"
          >
            {/* HEAL-106: имя файла без пробелов («HealthLab_analiz_…_1.pdf») не
                переносилось и уезжало под плашку статуса. Перенос внутри слова. */}
            <span className="min-w-0 text-hl-section font-semibold leading-snug [overflow-wrap:anywhere]">
              {uploadTitle(upload)}
            </span>
            <Pencil className="mt-1 h-3 w-3 flex-shrink-0 text-gray-400" />
          </button>
        )}

        <UploadStatusChip tone={status.tone} text={status.text} failure={upload.failure_reason} />
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-hl-small text-muted-foreground">
        {editingDate ? (
          <MaskedDateInput
            autoFocus
            value={upload.measured_at ?? ''}
            onChange={(value) => {
              // Длины мало: «31.02.2026» даст 10 символов, уедет на сервер, вернётся
              // 422 — а редактор к тому моменту уже закрыт, и введённое потеряно.
              // Тот же isRealIsoDate, что в форме визита.
              if (value.length === 10 && isRealIsoDate(value)) {
                setEditingDate(false);
                onSaveDate(value);
              }
            }}
            aria-label="Дата сдачи"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingDate(true)}
            disabled={saving || upload.status === 'processing'}
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline disabled:no-underline disabled:opacity-60"
          >
            {upload.measured_at ? formatDate(upload.measured_at) : 'Дата не указана'}
            <Pencil className="h-2.5 w-2.5" />
          </button>
        )}
        <span>·</span>
        <span>{formatSize(upload.file_size)}</span>
      </div>

      {/* Исходное имя файла — пункт 2 тикета. Показываем, только если название
          от него отличается: иначе строка дублировала бы заголовок. */}
      {/* HEAL-106: не режем многоточием — подсказка с полным именем на телефоне не
          открывается, а имя файла и есть то, по чему человек узнаёт свой бланк. */}
      {upload.title !== null && (
        <div className="text-hl-small text-gray-400 [overflow-wrap:anywhere]">Файл: {upload.original_filename}</div>
      )}
    </div>
  );
}

/**
 * Состав по РАСПОЗНАННОМУ, а не по записанным точкам. Нужен статусу `reported`:
 * жалоба подана до подтверждения, точек ещё нет, но показать человеку, на что он
 * жалуется, обязаны.
 */
function ReviewedComposition({
  markers,
  summary,
}: {
  markers: EnrichedBiomarker[];
  summary: ReviewSummary;
}) {
  if (markers.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Сводка</h2>
      <SummaryBarChart
        total={summary.total}
        normal={summary.normal}
        attention={summary.attention}
        outOfRange={summary.out_of_range}
        unknown={summary.unknown}
      />
      <h2 className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {markers.length} {pluralMarkers(markers.length)} из этого файла
      </h2>
      <div className="flex flex-col gap-1.5">
        {markers.map((marker, idx) => {
          const tier = reviewDisplayTier(marker.status, marker.optimum_status);
          const value = marker.value_qualitative
            ? capitalizeFirst(marker.value_qualitative)
            : marker.value !== null
              ? formatNumber(marker.value)
              : '—';

          return (
            <div
              key={`${marker.code ?? 'unmatched'}-${idx}`}
              className={`flex items-start justify-between gap-3 rounded-control border-l-[3px] bg-surface px-3 py-2 shadow-sm ${BORDER_LEFT_BY_TIER[tier]}`}
            >
              <div className="min-w-0 flex-1 text-hl-secondary font-medium leading-snug">
                {capitalizeFirst(marker.name_ru)}
              </div>
              <div className="flex-shrink-0 text-right">
                <span className={`text-hl-secondary font-semibold ${VALUE_TEXT_BY_TIER[tier]}`}>
                  {value}
                </span>
                {marker.unit && <span className="ml-1 text-hl-small text-gray-400">{marker.unit}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CompositionRow({ point }: { point: CompositionPoint }) {
  const tier = displayTier(point.clinical_status, point.optimum_status);
  const value =
    point.value_qualitative ??
    (point.value_normalized !== null
      ? formatNumber(point.value_normalized)
      : point.value_numeric !== null
        ? formatNumber(point.value_numeric)
        : '—');

  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-control border-l-[3px] bg-surface px-3 py-2 shadow-sm ${BORDER_LEFT_BY_TIER[tier]}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-hl-secondary font-medium leading-snug">
          {capitalizeFirst(point.name_ru)}
        </div>
        {point.reference_range_raw && (
          <div className="text-hl-small text-gray-400">Норма: {point.reference_range_raw}</div>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <span className={`text-hl-secondary font-semibold ${VALUE_TEXT_BY_TIER[tier]}`}>{value}</span>
        {point.unit && <span className="ml-1 text-hl-small text-gray-400">{point.unit}</span>}
      </div>
    </div>
  );
}

function DeleteDialog({
  open,
  onOpenChange,
  pointsCount,
  isReported,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pointsCount: number;
  isReported: boolean;
  pending: boolean;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить анализ?</AlertDialogTitle>
          <AlertDialogDescription>
            {pointsCount > 0
              ? `Вместе с файлом будут удалены ${pointsCount} ${pluralMarkers(pointsCount)} из этого анализа. Отменить это действие будет нельзя.`
              : 'Файл будет удалён. Отменить это действие будет нельзя.'}
            {isReported && (
              <>
                {' '}
                По этому анализу идёт наша проверка — после удаления мы не сможем её продолжить.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={pending}
            className="bg-rose-600 hover:bg-rose-700"
          >
            {pending ? 'Удаляем…' : 'Удалить'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ShiftDialog({
  warning,
  pending,
  onCancel,
  onConfirm,
}: {
  warning: { date: string; markers: number } | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={warning !== null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Изменить дату?</AlertDialogTitle>
          <AlertDialogDescription>
            {warning &&
              `Это изменит текущие значения по ${warning.markers} ${pluralMarkers(warning.markers)} на Главной: анализ переедет в истории, и последними станут другие результаты.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={pending}
          >
            {pending ? 'Сохраняем…' : 'Изменить'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function groupByCategory(points: CompositionPoint[]) {
  const map = new Map<string, { label: string; sortOrder: number; items: CompositionPoint[] }>();

  for (const point of points) {
    const entry = map.get(point.category_code) ?? {
      label: point.category_label_ru,
      sortOrder: point.sort_order,
      items: [],
    };
    entry.items.push(point);
    map.set(point.category_code, entry);
  }

  return Array.from(map.entries())
    .sort(([, a], [, b]) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
    .map(([category, entry]) => ({ category, label: entry.label, items: entry.items }));
}

/** Запятая как разделитель — то же написание, что на витринах. */
function formatNumber(value: number): string {
  return String(value).replace('.', ',');
}
